import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import './index.scss';

import { CustomButton } from '../../../../shared/uiComponents';
import { errorNormalizer } from '../../../../shared/Helpers/functions';
import { useBeforeUnloadWarning, useScreenWidth } from '../../../../shared/Helpers/hooks';
import Behaviours from './Behaviors';
import Targets from './targets';
import UnsavedTargetsWarning from './UnsavedTargetsWarning';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { clearSessionInfo } from '../../../../redux/State/clientSlice/sessionSlice';
import { clearErrors, createSoapNote } from '../../../../redux/State/clientSlice/soapNoteSlice';
import { setInfoEvent } from '../../../../redux/State/clientSlice/calendarHelperSlice';
import { TargetSessionStatuses } from '../../../../redux/API/ClientAPIHelpers/sessionsProperties';

const DataCollection = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const { clientId, sessionId } = useParams();
	const data = useSelector((state) => state.session.sessionInfo);
	const targets = useSelector((state) => state.session.targets);
	const isNavMenuOpen = useSelector((state) => state.common.isMenuOpen);

	const [showWarning, setShowWarning] = useState(false);
	const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const hasUnsavedTargets = targets.some((target) => target.targetStatus === TargetSessionStatuses.open);

	useBeforeUnloadWarning(hasUnsavedTargets);

	const navigateEditSoapNote = ({ reportId }: { clientId: string; reportId: string; sessionId: string }) => {
		dispatch(setInfoEvent(false));
		dispatch(clearSessionInfo());
		dispatch(clearErrors());

		navigate(`/soap-notes/info?clientId=${clientId}&reportId=${reportId}&sessionId=${sessionId}`);
	};

	const navigateToSoapNoteHandler = () => {
		if (!clientId || !sessionId) return;

		const reportId = data?.reportId;

		const performNavigation = () => {
			if (!reportId) {
				dispatch(createSoapNote({ clientId, sessionId }))
					.then(unwrapResult)
					.then(({ reportId: newReportId }) => {
						if (!newReportId) return;
						navigateEditSoapNote({ clientId, reportId: newReportId, sessionId });
					})
					.catch(errorNormalizer);

				return;
			}
			navigateEditSoapNote({ clientId, reportId, sessionId });
		};

		if (hasUnsavedTargets) {
			setPendingNavigation(() => performNavigation);
			setShowWarning(true);
		} else {
			performNavigation();
		}
	};

	const handleConfirm = () => {
		setShowWarning(false);

		if (pendingNavigation) {
			pendingNavigation();
			setPendingNavigation(null);
		}
	};

	const handleCancel = () => {
		setShowWarning(false);
		setPendingNavigation(null);
	};

	return (
		<>
			<Targets />
			<div
				className="targetsFooter"
				style={{ width: isMobile ? '100%' : isNavMenuOpen ? 'calc(100% - 372px)' : 'calc(100% - 84px)' }}
			>
				<Behaviours />
				<div className="action">
					<CustomButton title="Start SOAP Note" onClick={navigateToSoapNoteHandler} />
				</div>
			</div>
			<UnsavedTargetsWarning open={showWarning} onCancel={handleCancel} onConfirm={handleConfirm} />
		</>
	);
};

export default DataCollection;
